// src/pages/fiscalization/FiscalPointsPage.tsx
// Fiscal points (cash registers / fiscal devices): register, configure,
// deregister, and — in the rail — run their shift lifecycle.
//
// Covers: ListFiscalPoints, RegisterFiscalPoint, GetFiscalPoint,
// UpdateFiscalPoint, DeregisterFiscalPoint, plus (via ShiftsPanel)
// OpenShift, CloseShift, ListShifts, GetShiftReport.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useFiscalizationFiscalPoint, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';
import { cn } from '@core/ui';
import { SplitView } from '@/components/SplitView';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { FiscalPoint } from '@core/types/fiscalization';
import { formatDate, humanizeEnum, errMessage } from '@/features/fiscalization/format';
import { mapFiscalPointStatusToVariant } from '@/features/fiscalization/mapStatusToVariant';
import { RegisterFiscalPointModal, type RegisterFiscalPointValue } from '@/features/fiscalization/RegisterFiscalPointModal';
import { ShiftsPanel } from '@/features/fiscalization/ShiftsPanel';

type RailTab = 'details' | 'shifts';

export const FiscalPointsPage = observer(() => {
  const store = useFiscalizationFiscalPoint();
  const { t } = useI18n();
  const [selected, setSelected] = useState<FiscalPoint | null>(null);
  const [railTab, setRailTab] = useState<RailTab>('details');
  const [registerOpen, setRegisterOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [deregisterTarget, setDeregisterTarget] = useState<FiscalPoint | null>(null);
  const [deregisterReason, setDeregisterReason] = useState('');

  const load = () => {
    store.ListFiscalPoints({ pagination: { cursor: '', limit: 50, direction: 'forward' }, sort: [], date_range: {} as any, filters: {} as any } as any);
  };

  useEffect(() => { load(); }, []);

  const select = (p: FiscalPoint) => {
    setSelected(p);
    setRailTab('details');
    setEditName(p.name ?? '');
    setEditDescription(p.description ?? '');
  };

  const refreshSelected = async () => {
    if (!selected?.entity?.id) return;
    try {
      const fresh = await store.GetFiscalPoint(selected.entity.id);
      setSelected(fresh);
    } catch {
      // ignore
    }
  };

  const handleRegister = async (value: RegisterFiscalPointValue) => {
    await store.RegisterFiscalPoint({
      entity: {} as any,
      name: value.name,
      description: value.description,
      provider: { id: value.provider_id } as any,
      registration_number: value.registration_number,
      serial_number: value.serial_number,
      fiscal_drive_serial: value.fiscal_drive_serial,
      tax_authority_id: value.tax_authority_id,
      taxation_system: value.taxation_system,
      metadata: {},
    } as any);
    setRegisterOpen(false);
    load();
  };

  const handleSaveDetails = async () => {
    if (!selected?.entity?.id) return;
    setSavingEdit(true);
    setEditError(null);
    try {
      const updated = await store.UpdateFiscalPoint(selected.entity.id, {
        entity: selected.entity as any,
        field_mask: [],
        name: editName,
        description: editDescription,
        metadata: {},
      } as any);
      setSelected(updated);
      load();
    } catch (err) {
      setEditError(errMessage(err, t('fiscalization.points.saveError')));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeregister = async () => {
    if (!deregisterTarget?.entity?.id) return;
    await store.DeregisterFiscalPoint(deregisterTarget.entity.id, { reason: deregisterReason } as any);
    setDeregisterTarget(null);
    setDeregisterReason('');
    if (selected?.entity?.id === deregisterTarget.entity.id) setSelected(null);
    load();
  };

  const list = (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/fiscalization" className="text-muted-foreground hover:text-foreground">{t('fiscalization.dashboard.title')}</Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">{t('fiscalization.points.title')}</span>
          </nav>
          {/* The tab already names this page; the heading stays for screen readers. */}
          <h1 className="sr-only">{t('fiscalization.points.title')}</h1>
        </div>
        <Button onClick={() => setRegisterOpen(true)}>{t('fiscalization.points.register')}</Button>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('common.name')}</TableHead>
              <TableHead>{t('fiscalization.points.serialNumber')}</TableHead>
              <TableHead>{t('common.status')}</TableHead>
              <TableHead>{t('fiscalization.points.currentShift')}</TableHead>
              <TableHead>{t('fiscalization.points.totalReceipts')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8">{t('common.loading')}</TableCell></TableRow>
            ) : store.items.length === 0 ? (
              <TableEmpty colSpan={5}>
                <span className="table-empty-message">{t('fiscalization.points.empty')}</span>
                <p className="mt-1 text-sm text-muted-foreground">{t('fiscalization.points.subtitle')}</p>
              </TableEmpty>
            ) : (
              store.items.map((p) => (
                <TableRow key={p.entity?.id} className="cursor-pointer" onClick={() => select(p)}>
                  <TableCell>{p.name}</TableCell>
                  <TableCell className="font-mono text-xs">{p.serial_number}</TableCell>
                  <TableCell><Badge variant={mapFiscalPointStatusToVariant(p.status)} size="sm">{humanizeEnum(p.status, 'FISCAL_POINT_STATUS_')}</Badge></TableCell>
                  <TableCell>{p.current_shift_id ? <Badge variant="success" size="sm">{t('fiscalization.shifts.currentlyOpen')}</Badge> : <span className="text-muted-foreground">{t('fiscalization.shifts.currentlyClosed')}</span>}</TableCell>
                  <TableCell>{p.total_receipts}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );

  const detail = selected ? (
    <div className="p-6 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-foreground">{selected.name}</h2>
        <p className="text-sm text-muted-foreground">{selected.serial_number}</p>
      </div>

      <div className="flex items-center gap-1 border-b border-border" role="tablist">
        {(['details', 'shifts'] as RailTab[]).map((tb) => (
          <button
            key={tb}
            type="button"
            role="tab"
            aria-selected={railTab === tb}
            onClick={() => setRailTab(tb)}
            className={cn(
              'px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              railTab === tb ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {tb === 'details' ? t('fiscalization.common.details') : t('fiscalization.shifts.title')}
          </button>
        ))}
      </div>

      {railTab === 'details' ? (
        <div className="space-y-4">
          {editError && <p className="text-sm text-destructive">{editError}</p>}
          <Badge variant={mapFiscalPointStatusToVariant(selected.status)} size="sm">{humanizeEnum(selected.status, 'FISCAL_POINT_STATUS_')}</Badge>
          <Input label={t('common.name')} value={editName} onChange={(e) => setEditName(e.target.value)} />
          <Input label={t('common.description')} value={editDescription} onChange={(e) => setEditDescription(e.target.value)} />
          <dl className="divide-y divide-border text-sm">
            <div className="py-2 grid grid-cols-3 gap-2"><dt className="text-muted-foreground">{t('fiscalization.points.registrationNumber')}</dt><dd className="col-span-2">{selected.registration_number || '--'}</dd></div>
            <div className="py-2 grid grid-cols-3 gap-2"><dt className="text-muted-foreground">{t('fiscalization.points.taxationSystem')}</dt><dd className="col-span-2">{humanizeEnum(selected.taxation_system, 'TAXATION_SYSTEM_')}</dd></div>
            <div className="py-2 grid grid-cols-3 gap-2"><dt className="text-muted-foreground">{t('fiscalization.points.fiscalDriveExpires')}</dt><dd className="col-span-2">{formatDate(selected.fiscal_drive_expires_at)}</dd></div>
          </dl>
          <div className="flex gap-2">
            <Button size="sm" isLoading={savingEdit} onClick={handleSaveDetails}>{t('common.saveChanges')}</Button>
            <Button size="sm" variant="danger" onClick={() => setDeregisterTarget(selected)}>{t('fiscalization.points.deregister')}</Button>
          </div>
          {deregisterTarget?.entity?.id === selected.entity?.id && (
            <Input
              label={t('fiscalization.points.deregisterReason')}
              value={deregisterReason}
              onChange={(e) => setDeregisterReason(e.target.value)}
              placeholder={t('fiscalization.points.deregisterReasonPlaceholder')}
            />
          )}
        </div>
      ) : (
        <ShiftsPanel fiscalPoint={selected} onPointChanged={refreshSelected} />
      )}
    </div>
  ) : null;

  return (
    <>
      <SplitView detail={detail} onClose={() => setSelected(null)}>
        {list}
      </SplitView>

      <RegisterFiscalPointModal
        open={registerOpen}
        isSaving={store.isLoading}
        onCancel={() => setRegisterOpen(false)}
        onSubmit={handleRegister}
      />

      <ConfirmDialog
        open={!!deregisterTarget}
        title={t('fiscalization.points.deregisterConfirmTitle')}
        description={t('fiscalization.points.deregisterConfirmDesc')}
        destructive
        onConfirm={handleDeregister}
        onCancel={() => setDeregisterTarget(null)}
      />
    </>
  );
});

export default FiscalPointsPage;
